import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { FaImage, FaVideo } from "react-icons/fa";
import { IoIosImages } from "react-icons/io";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
  letter-spacing: 0.3px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  max-width: 820px;
  color: #4b5563;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #0f766e;
  color: #fff;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.03);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.35);
    outline-offset: 3px;
  }
`;

/* ---------- Folder grid ---------- */

const FolderGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
`;

const FolderCard = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 18px;
  cursor: pointer;
  padding: 16px;

  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  display: flex;
  align-items: center;
  gap: 14px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const FolderIconWrap = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(195, 122, 71, 0.12);
  border: 1px solid rgba(195, 122, 71, 0.22);
`;

const FolderIcon = styled(IoIosImages)`
  font-size: 34px;
  color: #c37a47;
`;

const FolderText = styled.div`
  min-width: 0;
  text-align: left;
`;

const FolderName = styled.div`
  font-weight: 900;
  color: #111827;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FolderMeta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
`;

/* ---------- Media grid ---------- */

const MediaGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const MediaCard = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 18px;
  cursor: pointer;
  padding: 0;
  overflow: hidden;

  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const Thumb = styled.img`
  width: 100%;
  height: 170px;
  object-fit: cover;
  display: block;

  @media (min-width: 900px) {
    height: 200px;
  }
`;

const MediaFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-top: 1px solid #f1f5f9;
`;

const MediaLabel = styled.div`
  font-weight: 900;
  font-size: 13px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MediaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 4px 10px;
  border-radius: 999px;
`;

/* ---------- Modal ---------- */

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 980px;
  max-height: 90vh;

  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 18px 70px rgba(0, 0, 0, 0.55);
  overflow: hidden;
`;

const ModalTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 12px 12px 16px;
  background: #0b1220;
  color: #fff;
`;

const ModalTitle = styled.div`
  font-weight: 900;
  font-size: 14px;
  opacity: 0.95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 900;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.55);
    outline-offset: 3px;
  }
`;

const ModalBody = styled.div`
  background: #000;
  touch-action: pan-y; /* tillåter scroll upp/ner, men vi tar swipe vänster/höger */
`;

const ModalImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const ModalIframe = styled.iframe`
  width: 100%;
  height: 60vh;
  border: 0;

  @media (max-width: 768px) {
    height: 45vh;
  }
`;

/* Nav buttons */
const NavBtn = styled.button`
  appearance: none;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  font-size: 28px;
  font-weight: 900;
  cursor: pointer;

  width: 46px;
  height: 46px;
  border-radius: 999px;

  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 3px;
  }

  @media (max-width: 520px) {
    width: 42px;
    height: 42px;
    font-size: 26px;
  }

  /* Valfritt: göm pilar på mobil när man kan swipe */
  @media (max-width: 680px) {
    display: none;
  }
`;

const PrevBtn = styled(NavBtn)`
  left: 12px;
`;

const NextBtn = styled(NavBtn)`
  right: 12px;
`;

/* ---------- Component ---------- */

const Gallery = () => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [modalMedia, setModalMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // Swipe states
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const SWIPE_THRESHOLD = 50;

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const snap = await getDocs(collection(firestore, "gallery"));
        const folderNames = snap.docs.map((doc) => doc.data().folder).filter(Boolean);
        const unique = [...new Set(folderNames)].sort((a, b) => a.localeCompare(b));
        setFolders(unique);
      } finally {
        setLoadingFolders(false);
      }
    };

    fetchFolders();
  }, []);

  useEffect(() => {
    const fetchMediaItems = async () => {
      if (!selectedFolder) return;

      setLoadingMedia(true);
      try {
        const q = query(
          collection(firestore, "gallery"),
          where("folder", "==", selectedFolder)
        );
        const snap = await getDocs(q);
        const mediaData = snap.docs.map((doc) => doc.data()).filter(Boolean);

        // valfri sort: images först
        mediaData.sort((a, b) => (a.type === b.type ? 0 : a.type === "image" ? -1 : 1));

        setMediaItems(mediaData);
      } finally {
        setLoadingMedia(false);
      }
    };

    fetchMediaItems();
  }, [selectedFolder]);

  const folderCountText = useMemo(() => {
    if (loadingFolders) return "Laddar mappar…";
    return folders.length ? `${folders.length} mappar` : "Inga mappar hittades ännu.";
  }, [loadingFolders, folders.length]);

  const handleBack = () => {
    setSelectedFolder(null);
    setMediaItems([]);
    setModalMedia(null);
    setCurrentIndex(null);
  };

  const closeModal = () => {
    setModalMedia(null);
    setCurrentIndex(null);
  };

  const showPrev = () => {
    if (currentIndex === null || !mediaItems.length) return;
    const prevIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setModalMedia(mediaItems[prevIndex]);
  };

  const showNext = () => {
    if (currentIndex === null || !mediaItems.length) return;
    const nextIndex = currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
    setModalMedia(mediaItems[nextIndex]);
  };

  // Keyboard: ESC + arrows
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!modalMedia) return;

      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalMedia, currentIndex, mediaItems, showPrev, showNext]);

  // Swipe handlers
const onTouchStart = (e) => {
  setTouchEndX(null);
  setTouchStartX(e.targetTouches[0].clientX);
};

  const onTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;

    const delta = touchStartX - touchEndX;

    if (delta > SWIPE_THRESHOLD) showNext();
    if (delta < -SWIPE_THRESHOLD) showPrev();

    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <>
      <SEO
        title="Galleri"
        description="Se bilder och video från våra evenemang och aktiviteter."
        keywords="galleri, bilder, video, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Galleri</Title>
              <Subtitle>
                {selectedFolder
                  ? `Du tittar på: ${selectedFolder}`
                  : `Välj en mapp och bläddra bland bilder och video. (${folderCountText})`}
              </Subtitle>
            </div>

            <Actions>
              {selectedFolder ? (
                <Btn type="button" onClick={handleBack}>
                  Tillbaka till mappar
                </Btn>
              ) : (
                <BtnGhost to="/">Tillbaka till startsidan</BtnGhost>
              )}
            </Actions>
          </Hero>

          {!selectedFolder && (
            <>
              {loadingFolders ? (
                <div style={{ color: "#6b7280", fontWeight: 800 }}>Laddar…</div>
              ) : (
                <FolderGrid>
                  {folders.map((folderName) => (
                    <FolderCard
                      key={folderName}
                      type="button"
                      onClick={() => setSelectedFolder(folderName)}
                      aria-label={`Öppna mapp ${folderName}`}
                    >
                      <FolderIconWrap>
                        <FolderIcon />
                      </FolderIconWrap>
                      <FolderText>
                        <FolderName title={folderName}>{folderName}</FolderName>
                        <FolderMeta>Klicka för att öppna</FolderMeta>
                      </FolderText>
                    </FolderCard>
                  ))}
                </FolderGrid>
              )}
            </>
          )}

          {selectedFolder && (
            <>
              {loadingMedia ? (
                <div style={{ color: "#6b7280", fontWeight: 800 }}>Laddar media…</div>
              ) : (
                <MediaGrid>
                  {mediaItems.map((media, index) => {
                    const isImage = media.type === "image";
                    const thumbSrc = isImage
                      ? media.url
                      : `https://img.youtube.com/vi/${media.videoId}/hqdefault.jpg`;

                    return (
                      <MediaCard
                        key={`${media.type}-${index}-${thumbSrc}`}
                        type="button"
                        onClick={() => {
                          setModalMedia(media);
                          setCurrentIndex(index);
                        }}
                        aria-label={isImage ? "Öppna bild" : "Öppna video"}
                      >
                        <Thumb
                          src={thumbSrc}
                          alt={isImage ? `Bild ${index + 1}` : `Video ${index + 1}`}
                        />
                        <MediaFooter>
                          <MediaLabel>
                            {isImage ? `Bild ${index + 1}` : `Video ${index + 1}`}
                          </MediaLabel>
                          <MediaBadge>
                            {isImage ? <FaImage /> : <FaVideo />}
                            {isImage ? "Bild" : "Video"}
                          </MediaBadge>
                        </MediaFooter>
                      </MediaCard>
                    );
                  })}
                </MediaGrid>
              )}
            </>
          )}
        </Container>

        {modalMedia && (
          <ModalBackdrop onClick={closeModal} role="dialog" aria-modal="true">
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTop>
                <ModalTitle>
                  {modalMedia.type === "image" ? "Bild" : "Video"} •{" "}
                  {selectedFolder || "Galleri"}{" "}
                  {currentIndex !== null && mediaItems.length
                    ? `• ${currentIndex + 1}/${mediaItems.length}`
                    : ""}
                </ModalTitle>
                <CloseBtn type="button" onClick={closeModal}>
                  Stäng ✕
                </CloseBtn>
              </ModalTop>

              {mediaItems.length > 1 && (
                <>
                  <PrevBtn type="button" onClick={showPrev} aria-label="Föregående">
                    ‹
                  </PrevBtn>
                  <NextBtn type="button" onClick={showNext} aria-label="Nästa">
                    ›
                  </NextBtn>
                </>
              )}

              <ModalBody
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {modalMedia.type === "image" ? (
                  <ModalImg src={modalMedia.url} alt="Galleri" />
                ) : (
                  <ModalIframe
                    src={`https://www.youtube.com/embed/${modalMedia.videoId}`}
                    title="Video"
                    allowFullScreen
                  />
                )}
              </ModalBody>
            </ModalContent>
          </ModalBackdrop>
        )}
      </Page>
    </>
  );
};

export default Gallery;








